import { SquareArrowUpRightIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { AddressFieldGroup } from "@/components/address-field-group";
import { BirthNumberAutocomplete } from "@/components/birth-number-autocomplete";
import { type Country, CountrySelect } from "@/components/country-select";
import { DocumentFieldGroup } from "@/components/document-field-group";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import type { PhysicalPersonsClientLink } from "@/types";

export interface PersonFormData {
	first_name: string;
	last_name: string;
	birth_number: string;
	street: string | null;
	city: string | null;
	postal_code: string | null;
	country: string | null;
	document_type: string | null;
	document_number: string | null;
	document_expiry_date: string | null;
	document_issuer: string | null;
	birth_date: string | null;
	birth_place: string | null;
	birth_country: string | null;
	citizenship: string | null;
	citizenship_other: string | null;
	gender: string | null;
	is_sanctioned: boolean | null;
	is_pep: boolean | null;
	pep_info: string | null;
}

export type SelectedPerson = {
	id: number;
	full_name: string;
	birth_number: string;
	client_links: PhysicalPersonsClientLink[];
	formData: PersonFormData;
};

export const PERSON_FIELD_LABELS = {
	first_name: "Jméno",
	last_name: "Příjmení",
	street: "Ulice",
	city: "Město",
	postal_code: "PSČ",
	country: "Země",
	document_type: "Typ dokladu",
	document_number: "Číslo dokladu",
	document_expiry_date: "Platnost do",
	document_issuer: "Vydavatel",
	birth_date: "Datum narození",
	birth_place: "Místo narození",
	birth_country: "Stát narození",
	citizenship: "Státní občanství",
	citizenship_other: "Jiné občanství",
	gender: "Pohlaví",
	is_sanctioned: "Na sankčním seznamu",
	is_pep: "Politicky exponovaná osoba",
	pep_info: "Informace o PEP",
} as const;

const CITIZENSHIP_OPTIONS = [
	{ value: "czech", label: "České" },
	{ value: "other", label: "Jiné" },
];

const GENDER_OPTIONS = [
	{ value: "male", label: "Muž" },
	{ value: "female", label: "Žena" },
];

const BOOLEAN_OPTIONS = [
	{ value: "true", label: "Ano" },
	{ value: "false", label: "Ne" },
	{ value: "null", label: "Nezjištěno" },
];

export interface PersonFormFieldsWarnings {
	birth_number?: boolean;
	address?: boolean;
	document?: boolean;
	document_expiring?: boolean;
	document_expired?: boolean;
	birth_info?: boolean;
	citizenship?: boolean;
	gender?: boolean;
	aml_check?: boolean;
}

interface PersonFormFieldsProps {
	value: PersonFormData;
	onChange: (data: PersonFormData) => void;
	errors: Partial<Record<string, string>>;
	countries: Country[];
	onPersonSelected: (person: SelectedPerson) => void;
	birthNumberRequired?: boolean;
	onClearError?: (field: keyof PersonFormData) => void;
	warnings?: PersonFormFieldsWarnings;
	excludePersonId?: number;
}

const booleanToSelectValue = (value: boolean | null): string => {
	if (value === true) return "true";
	if (value === false) return "false";
	return "null";
};

const selectValueToBoolean = (value: string): boolean | null => {
	if (value === "true") return true;
	if (value === "false") return false;
	return null;
};

export function PersonFormFields({
	value,
	onChange,
	errors,
	countries,
	onPersonSelected,
	birthNumberRequired = true,
	onClearError,
	warnings,
	excludePersonId,
}: PersonFormFieldsProps) {
	return (
		<>
			<Card>
				<CardHeader>
					<CardTitle>Osobní údaje</CardTitle>
				</CardHeader>
				<CardContent>
					<FieldGroup>
						<Field
							data-invalid={!!errors["physical_person.birth_number"]}
							data-warning={warnings?.birth_number && !value.birth_number}
						>
							<FieldLabel htmlFor="birth_number">
								Rodné číslo{" "}
								{birthNumberRequired && (
									<span className="text-destructive">*</span>
								)}
							</FieldLabel>
							<BirthNumberAutocomplete
								value={value.birth_number}
								onChange={(val) => {
									onClearError?.("birth_number");
									onChange({ ...value, birth_number: val });
								}}
								onPersonSelected={(person) => {
									onChange({ ...value, birth_number: person.birth_number });
									onPersonSelected({
										id: person.id,
										birth_number: person.birth_number,
										full_name: person.full_name,
										client_links: person.client_links,
										formData: {
											first_name: person.first_name,
											last_name: person.last_name,
											birth_number: person.birth_number,
											street: person.street,
											city: person.city,
											postal_code: person.postal_code,
											country: person.country,
											document_type: person.document_type,
											document_number: person.document_number,
											document_expiry_date: person.document_expiry_date,
											document_issuer: person.document_issuer,
											birth_date: person.birth_date,
											birth_place: person.birth_place,
											birth_country: person.birth_country,
											citizenship: person.citizenship,
											citizenship_other: person.citizenship_other,
											gender: person.gender,
											is_sanctioned: person.is_sanctioned,
											is_pep: person.is_pep,
											pep_info: person.pep_info,
										},
									});
								}}
								aria-invalid={!!errors["physical_person.birth_number"]}
								data-warning={warnings?.birth_number && !value.birth_number}
								excludeId={excludePersonId}
							/>
							<FieldError>{errors["physical_person.birth_number"]}</FieldError>
						</Field>
						<div className="grid grid-cols-2 gap-4">
							<Field data-invalid={!!errors["physical_person.first_name"]}>
								<FieldLabel htmlFor="first_name">
									Jméno <span className="text-destructive">*</span>
								</FieldLabel>
								<Input
									id="first_name"
									value={value.first_name}
									onChange={(e) =>
										onChange({ ...value, first_name: e.target.value })
									}
									placeholder="Jan"
									aria-invalid={!!errors["physical_person.first_name"]}
								/>
								<FieldError>{errors["physical_person.first_name"]}</FieldError>
							</Field>
							<Field data-invalid={!!errors["physical_person.last_name"]}>
								<FieldLabel htmlFor="last_name">
									Příjmení <span className="text-destructive">*</span>
								</FieldLabel>
								<Input
									id="last_name"
									value={value.last_name}
									onChange={(e) =>
										onChange({ ...value, last_name: e.target.value })
									}
									placeholder="Novák"
									aria-invalid={!!errors["physical_person.last_name"]}
								/>
								<FieldError>{errors["physical_person.last_name"]}</FieldError>
							</Field>
						</div>
					</FieldGroup>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Údaje o narození</CardTitle>
				</CardHeader>
				<CardContent>
					<FieldGroup>
						<Field data-warning={warnings?.birth_info && !value.birth_date}>
							<FieldLabel htmlFor="birth_date">Datum narození</FieldLabel>
							<DatePicker
								id="birth_date"
								value={value.birth_date}
								onChange={(birth_date) => onChange({ ...value, birth_date })}
								fromDate={new Date(1900, 0, 1)}
								toDate={new Date()}
								data-warning={warnings?.birth_info && !value.birth_date}
							/>
						</Field>
						<div className="grid grid-cols-2 gap-4">
							<Field data-warning={warnings?.birth_info && !value.birth_place}>
								<FieldLabel htmlFor="birth_place">Místo narození</FieldLabel>
								<Input
									id="birth_place"
									value={value.birth_place || ""}
									onChange={(e) =>
										onChange({
											...value,
											birth_place: e.target.value || null,
										})
									}
									placeholder="Praha"
									data-warning={warnings?.birth_info && !value.birth_place}
								/>
							</Field>
							<Field
								data-warning={warnings?.birth_info && !value.birth_country}
							>
								<FieldLabel htmlFor="birth_country">Stát narození</FieldLabel>
								<CountrySelect
									value={value.birth_country || ""}
									onChange={(birth_country) =>
										onChange({ ...value, birth_country })
									}
									countries={countries}
									data-warning={warnings?.birth_info && !value.birth_country}
								/>
							</Field>
						</div>
					</FieldGroup>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Občanství a pohlaví</CardTitle>
				</CardHeader>
				<CardContent>
					<FieldGroup>
						<div className="grid grid-cols-2 gap-4">
							<Field data-warning={warnings?.citizenship && !value.citizenship}>
								<FieldLabel htmlFor="citizenship">Státní občanství</FieldLabel>
								<Select
									value={value.citizenship || ""}
									onValueChange={(citizenship) =>
										onChange({
											...value,
											citizenship: citizenship || null,
											citizenship_other:
												citizenship !== "other"
													? null
													: value.citizenship_other,
										})
									}
									items={[
										{ value: "", label: "Vyberte" },
										...CITIZENSHIP_OPTIONS,
									]}
								>
									<SelectTrigger
										className="w-full"
										data-warning={warnings?.citizenship && !value.citizenship}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{CITIZENSHIP_OPTIONS.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</Field>
							{value.citizenship === "other" && (
								<Field>
									<FieldLabel htmlFor="citizenship_other">
										Jiné občanství
									</FieldLabel>
									<Input
										id="citizenship_other"
										value={value.citizenship_other || ""}
										onChange={(e) =>
											onChange({
												...value,
												citizenship_other: e.target.value || null,
											})
										}
										placeholder="Německé"
									/>
								</Field>
							)}
						</div>
						<Field data-warning={warnings?.gender && !value.gender}>
							<FieldLabel htmlFor="gender">Pohlaví</FieldLabel>
							<Select
								value={value.gender || ""}
								onValueChange={(gender) =>
									onChange({ ...value, gender: gender || null })
								}
								items={[{ value: "", label: "Vyberte" }, ...GENDER_OPTIONS]}
							>
								<SelectTrigger
									className="w-full"
									data-warning={warnings?.gender && !value.gender}
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{GENDER_OPTIONS.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</Field>
					</FieldGroup>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Bydliště</CardTitle>
				</CardHeader>
				<CardContent>
					<AddressFieldGroup
						value={{
							street: value.street,
							city: value.city,
							postal_code: value.postal_code,
							country: value.country,
						}}
						onChange={(address) => onChange({ ...value, ...address })}
						countries={countries}
						invalid={warnings?.address}
						errors={{
							street: errors["physical_person.street"],
							city: errors["physical_person.city"],
							postal_code: errors["physical_person.postal_code"],
							country: errors["physical_person.country"],
						}}
						onClearError={(field) =>
							onClearError?.(field as keyof PersonFormData)
						}
					/>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Doklad totožnosti</CardTitle>
				</CardHeader>
				<CardContent>
					<DocumentFieldGroup
						value={{
							document_type: value.document_type,
							document_number: value.document_number,
							expiry_date: value.document_expiry_date,
							issuer: value.document_issuer,
						}}
						onChange={(doc) =>
							onChange({
								...value,
								document_type: doc.document_type,
								document_number: doc.document_number,
								document_expiry_date: doc.expiry_date,
								document_issuer: doc.issuer,
							})
						}
						invalid={warnings?.document}
						expiryWarning={warnings?.document_expiring}
						expiryExpired={warnings?.document_expired}
						errors={{
							document_type: errors["physical_person.document_type"],
							document_number: errors["physical_person.document_number"],
							expiry_date: errors["physical_person.document_expiry_date"],
							issuer: errors["physical_person.document_issuer"],
						}}
						onClearError={(field) => {
							const fieldMap: Record<string, keyof PersonFormData> = {
								document_type: "document_type",
								document_number: "document_number",
								expiry_date: "document_expiry_date",
								issuer: "document_issuer",
							};
							onClearError?.(fieldMap[field]);
						}}
					/>
				</CardContent>
			</Card>

			<Card className="mb-12">
				<CardHeader>
					<div className="flex items-center justify-between gap-2">
						<CardTitle>AML status</CardTitle>
						<div className="flex gap-2">
							<Button
								type="button"
								variant="outline"
								size="sm"
								render={
									<a
										href={`https://www.amlsolutions.cz/overovani-mezinarodnich-sankci?person=${encodeURIComponent(`${value.first_name} ${value.last_name}`.trim())}#:~:text=Zde%20zadejte%20jm%C3%A9no%20a%20p%C5%99%C3%ADjmen%C3%AD%20klienta`}
										target="_blank"
										rel="noopener noreferrer"
									/>
								}
							>
								<HugeiconsIcon
									icon={SquareArrowUpRightIcon}
									strokeWidth={2}
									data-icon="inline-start"
								/>
								Sankce
							</Button>
							<Button
								type="button"
								variant="outline"
								size="sm"
								render={
									<a
										href="https://www.pepcheck.cz/cs#:~:text=Za%C4%8Dn%C4%9Bte%20vyhled%C3%A1vat%20PEP%20i%20osoby%20na%20sank%C4%8Dn%C3%ADch%20listech"
										target="_blank"
										rel="noopener noreferrer"
									/>
								}
							>
								<HugeiconsIcon
									icon={SquareArrowUpRightIcon}
									strokeWidth={2}
									data-icon="inline-start"
								/>
								PEP
							</Button>
						</div>
					</div>
				</CardHeader>
				<CardContent>
					<FieldGroup>
						<div className="grid grid-cols-2 gap-4">
							<Field
								data-warning={
									warnings?.aml_check && value.is_sanctioned === null
								}
							>
								<FieldLabel htmlFor="is_sanctioned">
									Na sankčním seznamu
								</FieldLabel>
								<Select
									value={booleanToSelectValue(value.is_sanctioned)}
									onValueChange={(val) =>
										onChange({
											...value,
											is_sanctioned: selectValueToBoolean(val || "null"),
										})
									}
									items={BOOLEAN_OPTIONS}
								>
									<SelectTrigger
										className="w-full"
										data-warning={
											warnings?.aml_check && value.is_sanctioned === null
										}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{BOOLEAN_OPTIONS.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</Field>
							<Field
								data-warning={warnings?.aml_check && value.is_pep === null}
							>
								<FieldLabel htmlFor="is_pep">
									Politicky exponovaná osoba
								</FieldLabel>
								<Select
									value={booleanToSelectValue(value.is_pep)}
									onValueChange={(val) => {
										const boolVal = selectValueToBoolean(val || "null");
										onChange({
											...value,
											is_pep: boolVal,
											pep_info: boolVal !== true ? null : value.pep_info,
										});
									}}
									items={BOOLEAN_OPTIONS}
								>
									<SelectTrigger
										className="w-full"
										data-warning={warnings?.aml_check && value.is_pep === null}
									>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{BOOLEAN_OPTIONS.map((option) => (
											<SelectItem key={option.value} value={option.value}>
												{option.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</Field>
						</div>
						{value.is_pep === true && (
							<Field>
								<FieldLabel htmlFor="pep_info">
									Jméno funkce a doba trvání:
								</FieldLabel>
								<Input
									id="pep_info"
									value={value.pep_info || ""}
									onChange={(e) =>
										onChange({
											...value,
											pep_info: e.target.value || null,
										})
									}
									placeholder="Např. poslanec, 2020-2024"
								/>
							</Field>
						)}
					</FieldGroup>
				</CardContent>
			</Card>
		</>
	);
}
